import { PaymentPlanSKU } from 'core/services/bff';

import type { Combo } from './combo.d';
import type { AsyncFlow } from './utilities';
import type {
  Item as ItemProduct,
  Price,
  UnavailableItemImages,
} from 'core/services/bff/@types/cart/cart.d';
import type { PaymentPlansByProduct } from 'ui/pages/Checkout/domains/specialPayment';

export enum CART_SHIPPING_MODEL_VIEW {
  OPTIONS = 'options',
  SELECT_PICKUP_STORE = 'selectPickupStore',
  PRODUCT_NOT_FOUND = 'productNotFound',
}

type Coupon = {
  couponIsApplied?: boolean | null;
  couponNumber?: string | null;
  description: string;
  isValidCoupon?: boolean | null;
  isHiddenButtonError?: boolean;
  isHiddenButtonSuccess?: boolean;
  isHiddenButtonDeleteSuccess?: boolean;
};

type Current = {
  code: string;
  distributionCenterAddress: string;
  shippingModel: {
    businessModelCode?: number;
    changeDeliveryMode: boolean;
    distributionCenterCode?: number;
    iswithdrawal: boolean;
  };
  shippingModelType?: string;
  userCredit: string;
};

type DeleteConfirmation = {
  data: DeleteItem;
  isOpen: boolean;
};

type modalConfirmation = {
  isOpen: boolean;
};

type DeleteItem = Pick<
  Item,
  'image' | 'name' | 'productCode' | 'productPosition' | 'productOrigin'
>;

export type CartItemBase = {
  productCode: string;
  productOrigin?: number;
  productPosition?: number;
  productType?: string;
  quantity: number;
  itemListName?: string;
};

export type Image = {
  imageSize?: string;
  title?: string;
  url?: string;
};

export type Item = CartItemBase & {
  brand?: string;
  department?: string;
  description: string;
  discount?: number;
  image: Image;
  inventory: number;
  isAvailable: boolean;
  isCustomCombo: boolean;
  name: string;
  previousQuantity: number;
  previousQtd?: number;
  hasBeenDeletedSuccessfully?: boolean;
  price?: string;
  priceNum: number;
  priceTotal: string;
  priceTotalNum: number;
  productIndex?: number;
};

type PaymentInfoBase = {
  creditCardSchema?: number;
  installments: number;
  paymentCode: string;
  paymentMode: string;
};

export type PaymentInfo = PaymentInfoBase & {
  administrateTax: string;
  administrateTaxNum: number;
  description: string;
  flagId?: string;
  isBlocked: boolean;
  name: string;
  paymentPlanType: string;
  paymentPlansByProduct?: PaymentPlansByProduct[];
};

type AvailableItemsPoints = Pick<
  Item,
  'productCode' | 'quantity' | 'description' | 'name' | 'image' | 'productOrigin'
> & { productPosition: number };

type PointsPrograms = {
  availableItems: AvailableItemsPoints[];
  code: number;
  name: string;
  orderUsedPoints: number;
  remainingPoints: number;
};

export type CutDetail = {
  userMessage: string;
  eventLevel: string;
};

export type UnavailableItem = {
  sku: string;
  name: string;
  quantity: number;
  image: UnavailableItemImages[];
  change?: boolean;
  detail?: CutDetail;
};

type Data = {
  success: boolean;
  cashback: string;
  cashbackNum: number;
  code: string;
  coupon: Coupon;
  items: Item[];
  unavailableItems: UnavailableItem[];
  minOrderPercentReached: string;
  minOrderPercentReachedNum: number;
  minOrderValue: string;
  minOrderValueNum: number;
  minOrderValuePending: string;
  minOrderValuePendingNum: number;
  paymentDetail: Partial<PaymentInfo>;
  paymentOption?: PaymentInfoBase['paymentCode'];
  pointsPrograms: PointsPrograms[];
  shipping: string;
  shippingNum: number;
  subTotal: string;
  subTotalNum: number;
  total: string;
  totalDiscount: string;
  totalDiscountNum: number;
  totalItems: number;
  totalNum: number;
  totalProfit: string;
  totalProfitNum: number;
  totalUnitCount: number;
  statusCode?: number;
  paymentPlans?: PaymentPlanSKU[];
};

type ShippingModelViews = {
  options: ShippingModelValueTypes;
  selectPickupStore: ShippingModelValueTypes;
  pickupStoreNotFound: ShippingModelValueTypes;
  productNotFound: ShippingModelValueTypes;
};

export type ShippingModelValueTypes = ValueOf<typeof CART_SHIPPING_MODEL_VIEW>;

type CartDefaultStep = keyof typeof ShippingModelViews;

export type Cart = {
  canAddItem: boolean;
  canSubmit: boolean;
  canSubmitShippingModel: boolean;
  shippingModelHasBeenSelected: boolean;
  couponUpdate: AsyncFlow & { meta: { couponNumber?: string | null } };
  current: Current;
  data: Data;
  deleteConfirmation: DeleteConfirmation;
  fetch: AsyncFlow;
  hasBeenCalled: boolean;
  hasBeenDeletedSuccessfully?: boolean;
  shippingModelNotFound: boolean;
  hasItem: boolean;
  isRunning: boolean;
  itemUpdate: AsyncFlow & { meta: CartItemBase };
  minOrderConfirmation: modalConfirmation;
  comboEditConfirmation: modalConfirmation & { comboCode: Combo['data']['comboCode'] };
  step: CartDefaultStep;
  updatedAt: number;
};

type AvailableItemProcessed = ItemProduct & {
  quantity: number;
  sku: number;
};

type PriceProcessed = Price & {
  collectionFee: number;
  debit: number;
  subTotal: number;
  collectionFee: number;
  debit: number;
  subTotal: number;
};

export type CartProcessUpdated = {
  availableItems: AvailableItemProcessed[];
  price: PriceProcessed;
};
